/**
 * Shared helpers for dxfBridge.dynamic.* — group lookup, numeric parsing,
 * point extraction, and the float-formatting convention used by the
 * writer. Extracted to keep the top-level `dxfBridge.dynamic.ts` under
 * the 1000-line guardrail while allowing the parser / writer siblings
 * to stay small and focused.
 */
import { decodeDxfUnicode, type DxfGroup } from "@dxf-ai-editor/dxf-io";

/**
 * T9 PR-3 — decode `\U+XXXX` on the CONSUMER view of a dynamic-block rawGroups
 * value. The dxf-io writer chokepoint escapes string-content codes (1/2/3/4/
 * 300/301/302/…) when it re-emits the dynamic-block OBJECTS, so a Cyrillic
 * parameter / property-table NAME or string row value would re-import as a
 * literal `\U+…` (and the rebuild-on-write path would then DOUBLE-escape it,
 * corrupting the round-trip) unless the parser decodes it here.
 *
 * `firstGroup` / `collectGroups` are the single chokepoint every dynamic-block
 * parser uses to read a string value out of `obj.rawGroups` (tier1/2/3 names,
 * labels, descriptions, property-table cells). Decoding here is by-construction
 * complete for every such read and is a guaranteed NO-OP on the handle (340/341/
 * 330 — hex ASCII) and numeric (140/170/90 — read via firstNumber/firstInt, which
 * decode then parse) values these helpers also serve, because `decodeDxfUnicode`
 * returns its input unchanged when no `\U+` escape is present. The raw `g.value`
 * is never mutated, so the writer's verbatim rawGroups re-export stays
 * byte-identical (decode-on-read → re-escape-on-write = same bytes).
 */
export function firstGroup(
  groups: DxfGroup[],
  code: number,
): string | undefined {
  for (const g of groups) {
    if (g.code === code) return decodeDxfUnicode(g.value);
  }
  return undefined;
}

export function firstNumber(
  groups: DxfGroup[],
  code: number,
): number | undefined {
  const raw = firstGroup(groups, code);
  if (raw === undefined) return undefined;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : undefined;
}

export function firstInt(groups: DxfGroup[], code: number): number | undefined {
  const raw = firstGroup(groups, code);
  if (raw === undefined) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}

export function collectGroups(groups: DxfGroup[], code: number): string[] {
  // T9 PR-3 — decode the CONSUMER view (see firstGroup). No-op on handle /
  // numeric values; raw `g.value` untouched so re-export stays byte-identical.
  const out: string[] = [];
  for (const g of groups) {
    if (g.code === code) out.push(decodeDxfUnicode(g.value));
  }
  return out;
}

export function parsePointAt(
  groups: DxfGroup[],
  xCode: number,
  yCode: number,
): { x: number; y: number } {
  return {
    x: firstNumber(groups, xCode) ?? 0,
    y: firstNumber(groups, yCode) ?? 0,
  };
}

/**
 * Push a typed group onto the accumulator using the same float-format
 * convention as `packages/dxf-io/src/writer.ts` (fixed(12) trimmed to
 * avoid trailing zeros / "-0"). Non-finite numbers are silently
 * skipped — emitting "NaN" would corrupt the DXF text.
 */
export function pushGroup(
  groups: DxfGroup[],
  code: number,
  value: string | number,
): void {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return;
    if (Number.isInteger(value)) {
      groups.push({ code, value: String(value) });
    } else {
      const fixed = value.toFixed(12).replace(/\.?0+$/, "");
      groups.push({ code, value: fixed === "-0" ? "0" : fixed });
    }
    return;
  }
  groups.push({ code, value });
}

/**
 * AutoCAD writes dynamic-block evaluator classes in two spellings:
 *   1. Prefixed   — `ACDBBLOCKLINEARPARAMETER`, `ACDBDYNAMICBLOCKPURGEPREVENTER`
 *   2. Un-prefixed — `BLOCKLINEARPARAMETER`, `BLOCKPROPERTIESTABLE`
 *
 * The un-prefixed form appears primarily in AutoCAD 2018+ output for
 * parametric (constraint-based) blocks. This helper normalises both
 * spellings to a single canonical form so the downstream switch/Set
 * lookups don't need two entries for each class.
 *
 * Idempotent: already-canonical strings are returned as-is.
 */
export function canonicalDynamicBlockClass(type: string): string {
  if (type.startsWith("ACDB")) return type;
  if (type.startsWith("BLOCK")) return "ACDB" + type;
  return type;
}

/**
 * Decode inline per-state invisible-entity handles from a
 * `BLOCKVISIBILITYPARAMETER`'s group array by SEQUENTIAL walk.
 *
 * SHARED helper consumed by BOTH the prefixed-path parser
 * (`dxfBridge.dynamic.parser.ts` — pass 1) and the un-prefixed
 * fallback (`dxfBridge.dynamic.visibility.ts` — pass 2). Both paths
 * encounter AutoCAD 2018+ records where the per-state invisible
 * entity handles are encoded INLINE on the parameter record itself
 * rather than via child BLOCKVISIBILITYSTATE records.
 *
 * Background: AutoCAD 2018+ (observed in АБИ1.dxf, e.g. "Двутавр ГОСТ
 * 8239-89" block) encodes visibility-state geometry INLINE within the
 * BVP record itself — NOT via child BLOCKVISIBILITYSTATE records. The
 * encoding layout inside the BVP groups after the 281/301/302 header:
 *
 *   91 0                       — reserved
 *   93 <N>                     — total block entity count
 *   331 <handle> × N           — block-wide entity handle list (ignored
 *                                here; evaluator consumes via its own
 *                                handle table)
 *   92 <S>                     — state count
 *   // repeated S times:
 *   303 <stateName>            — state name marker
 *   94  <invisibleCount>       — number of invisible entity handles
 *   332 <handle> × <inv>       — invisible entity handles FOR THIS STATE
 *   95  <extraCount>           — extra refs count
 *   333 <handle> × <extra>     — extra handles (NOT visibility; nested
 *                                block-feature refs — ignore)
 *
 * The key property is that group 332 codes APPEARING AFTER a 303 marker
 * belong to that state's invisible-entity list. Group 333 codes are
 * separate (nested block/feature references) and never feed state
 * visibility. Block-wide 331 codes appear BEFORE any 303, so state
 * tracking is null and they're ignored naturally.
 *
 * Returns a Map from stateName → invisibleEntityIds in authoring order.
 * Duplicate state names preserve the FIRST occurrence's entity list
 * (consistent with the 303 dedup policy in both parser paths).
 * Empty-string state names are skipped.
 */
export function decodeInlineStatesFromBvpGroups(
  groups: readonly DxfGroup[],
): Map<string, readonly string[]> {
  const out = new Map<string, string[]>();
  let currentName: string | null = null;
  let currentList: string[] | null = null;
  for (const g of groups) {
    if (g.code === 303) {
      const name = g.value.trim();
      if (name.length === 0) continue;
      // First-occurrence wins — if we already have this state name,
      // subsequent duplicate 303 blocks get their own scratch list that
      // is NOT written back to the map (preserves authored entity set).
      if (out.has(name)) {
        currentName = name;
        currentList = [];
      } else {
        currentName = name;
        currentList = [];
        out.set(name, currentList);
      }
    } else if (g.code === 332 && currentName !== null && currentList !== null) {
      const handle = g.value.trim();
      if (handle.length > 0) currentList.push(handle);
    }
    // All other codes (91, 92, 93, 94, 95, 331, 333, ...) are either
    // structural metadata or out of scope for state-visibility and are
    // ignored. We do NOT use 94 as a hard boundary — the 303 marker is
    // the boundary signal and is strictly sufficient.
  }
  return out;
}
